import { useFeature } from 'feature-toggle-jsx';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as R from 'ramda';
import { useTranslation } from 'react-i18next';
import useInterval from 'react-useinterval';
import { useNavigate } from 'react-router-dom';
import { CustomError, TechnicalError } from '../common/errors';
import Log from '../common/logging';
import Result, { CancellableResult } from '../common/result';
import WindowProvider from '../common/window';
import { Action, Provider, Resolver, SipConfig } from '../config';
import { Routes } from '../config/routes';
import { EventPayload } from '../events';
import { FeatureToggleValue } from '../features/featureToggles';
import metadataCollector from '../services/metadata';
import validationService, {
  Step,
  Validation,
  ValidationStatus,
  ValidationStep,
  ValidationStepStatus,
} from '../services/validation';
import { AuthenticationContext, LoginDeviceTokenResult } from './AuthenticationContext';
import { FlowConfigContext } from './FlowConfigContext';
import BackgroundInitialization from './validation/background/BackgroundInitialization';
import ValidationBackgroundProcessing from './validation/background/ValidationBackgroundProcessing';
import validationConfig from './validation/validationConfig';
import { ContextValidationStatus, ExtendedValidationStatuses } from './validation/contextValidationStatus';
import FlowMode from './validation/FlowMode';
import DefaultValidationEventBuilder from './validation/result/DefaultValidationEventBuilder';
import ValidationEventBuilder from './validation/result/ValidationEventBuilder';
import StepError, { isInvalidStepError } from './validation/stepError';
import StepWorker from './validation/steps/StepWorker';
import unauthenticatedStatus from './validation/unauthenticatedStatus';
import ValidationError from './validation/validationError';
import validationRouting from './validation/validationRouting';
import { isRemoteModeSettable } from '../services/common/remoteModeSettable';
import unauthenticatedRouting from './validation/unauthenticatedRouting';
import { ScopeRequestContext } from './ScopeRequestContext';
import { ScopeRequest } from '../services/secureIdentityProvider';
import {
  GatekeeperNetworkServiceContext,
  useGKNService,
} from './GatekeeperNetworkService/GatekeeperNetworkServiceContext';
import StatusEventPayload from '../services/events/statusEventPayload';
import { getGknLocalisedString, getGknValue } from './GatekeeperNetworkService/utils';
import { RequiredDeclarations } from './GatekeeperNetworkService/GatekeeperNetworkServiceData';
import { Credentials, ReuseStatus } from '../common/types';
import { useCredentialStore } from './CredentialStoreContext';
import DynamicValidationService from '../services/validation/dynamicValidationService';
import { ExchangeTokenData } from '../services/authentication';
import { setStorageItemForNetwork, StorageKey } from '../utils/storage';

export type EventPayloadResult = Result<CustomError, EventPayload>;

export interface ValidationProps {
  /** Is web document flow enabled */
  isWebFlowEnabled: boolean;
  /** The current validation id */
  validationId: string | null;
  /** Errors in the current validation */
  validationErrors: string[];
  /** Perform initialization in the background */
  backgroundInitialize(): void;
  /** Get the current status of a users validation without authenticating the user */
  getStatusUnauthenticated(): Promise<Result<CustomError, string>>;
  /** Activate exchange token and seed validation context  */
  activateDeviceLinkToken(linkToken: string): Promise<LoginDeviceTokenResult>;
  /** Get latest validation status and update pending steps */
  updateValidationPendingSteps(): Promise<Result<CustomError, ValidationStep[]>>;
  /** Get route for next validation step
   * the 'null' result represents the status where we 'hijack' the current validation process to store credentials
   * we will resume the flow after the credentials are stored by passing the route as a path in state
   */
  getRouteForNextStep(): Promise<Routes | null>;
  /** Reset the pending steps for the given route */
  resetPendingStepsForRoute(currentRoute: Routes): void;
  /** Delete an existing validation */
  deleteVerification(
    skipOnPartialFlowMode?: boolean,
    skipNotFailedValidation?: boolean,
  ): Promise<Result<CustomError, boolean>>;
  /** Process a step, handle errors and enqueue background work */
  processStep(stepWorker: StepWorker): Promise<CancellableResult<CustomError>>;
  /** Get the resulting event payload */
  getEventPayload(scopeRequestPayload?: EventPayload): Promise<EventPayloadResult>;
  /**  Return the route based on the current validation status
   *  the 'null' result represents the status where we 'hijack' the current validation process to store credentials
   *  we will resume the flow after the credentials are stored by passing the route as a path in state
   */
  getRouteForCurrentStatus(): Promise<Routes | null>;
  /** Return a flag indicating if the verification has started */
  verificationStarted(): boolean;
  /** The current validation status */
  validationStatus: ExtendedValidationStatuses;
  /** Get the latest validation process status and errors and update them in state */
  updateValidationStatus(): Promise<void>;
  /** Wait for all background tasks to finish */
  waitForBackgroundProcessing(): Promise<Result<CustomError>>;
  /** Set the flow mode */
  setFlowMode(mode: FlowMode): void;
  /** The current flow mode */
  flowMode: FlowMode;
  /** Return a list of pending routes that can be continued on mobile */
  getPendingContinueOnMobileRoutes(): Array<Routes>;
  /** Return details for a failed step when status is failedStepInvalid */
  failedStep: StepError | null;
  /** Whether or not the continue on mobile is enable on the flow */
  showContinueOnMobile: boolean;
  /** when a linked device is connected, re-add the previous step since it will be executed
   *  in the other device and not on the current anymore. */
  processLinkedDeviceConnected: () => void;
  getCurrentCredentials: (inValidationId?: string | null) => Promise<Result<CustomError, Credentials>>;
  fetchStatus?: 'in_progress' | 'complete';
  shouldInitiateCredentialStorage: (inValidationId?: string | null) => Promise<boolean>;
  hasNewCredentialsToStore: (inValidationId?: string | null) => Promise<boolean>;
  deviceLinkContext: ExchangeTokenData | null;
}

export const ValidationContext = createContext<ValidationProps>({} as ValidationProps);

type ValidationProviderProps = {
  children: JSX.Element;
};

/** Load the polling delay from config */
export const getPollingDelayMilliseconds = (
  status: ExtendedValidationStatuses = ContextValidationStatus.unknown,
): number => {
  switch (status) {
    // Perform the first poll almost immediately after setting the status to poll to get the actual status of the validation
    case ContextValidationStatus.poll:
      return 10;
    default:
      return Resolver.getServiceConfig().validation.pollingIntervalInSeconds * 1000;
  }
};

/**
 *
 * @param props
 * @constructor
 */
export function ValidationProvider(props: ValidationProviderProps): JSX.Element {
  const {
    action,
    networkAddress,
    networkName,
    chainName,
    provider,
    getProviderConfig,
    setAcceptedDeclaration,
    setAcceptedTermsAndConditionsLink,
    setAcceptedPrivacyPolicyLink,
    setAcceptedBiometricsLink,
    selectProvider,
    selectNetworkAddress,
    flowInProgress,
    initialized,
    flowFailed,
    validationStatusFetchComplete,
    showPIIShareAuthorization,
    getOptionalFlowExecutionConfig,
    credentialExpired,
  } = useContext(FlowConfigContext);
  const {
    isLoggedIn,
    getCurrentToken: currentToken,
    getUserData,
    loginWithDeviceLinkToken,
  } = useContext(AuthenticationContext);
  const { supportsCredentialReuse } = useGKNService();
  const navigate = useNavigate();
  const { allCredentialsStored, reuseStatus, existingStoredCredentials, chosenCredentialIds } = useCredentialStore();
  const { gknServiceData } = useContext(GatekeeperNetworkServiceContext);
  const { t } = useTranslation();
  const [flowMode, setFlowMode] = useState<FlowMode>(FlowMode.userSelect);
  const [validationId, setValidationId] = useState<string | null>(null);
  const [validationStatus, setValidationStatus] = useState<ExtendedValidationStatuses>(ContextValidationStatus.unknown);
  const [pendingSteps, setPendingSteps] = useState<Array<ValidationStep>>([]);
  const [currentStep, setCurrentStep] = useState<ValidationStep>();
  const [pollingDelay, setPollingDelay] = useState<number | null>(null);
  const [backgroundProcessing] = useState<ValidationBackgroundProcessing>(new ValidationBackgroundProcessing());
  const [backgroundInitialization] = useState<BackgroundInitialization>(new BackgroundInitialization());
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [failedStep, setFailedStep] = useState<StepError | null>(null);
  const [deviceLinkContext, setDeviceLinkContext] = useState<ExchangeTokenData | null>(null);
  const [validationEventBuilder, setValidationEventBuilder] = useState<ValidationEventBuilder>(
    new DefaultValidationEventBuilder(),
  );
  const [forceNewValidation, setForceNewValidation] = useState<boolean>(false);
  const [showContinueOnMobile, setShowContinueOnMobile] = useState<boolean>(false);
  const scopeRequestContext = useContext(ScopeRequestContext);
  const [fetchStatus, setFetchStatus] = useState<'in_progress' | 'complete'>();
  const { children } = props;

  const [enableContinueOnMobile] = useFeature('continueOnMobile', (f: FeatureToggleValue): boolean => {
    return (f.isEnabled || false) && WindowProvider.isOnDesktop();
  });

  const [validationScopeRequestEnabled] = useFeature(
    'validationScopeRequest',
    (f: FeatureToggleValue): boolean =>
      (f.isEnabled || false) &&
      // Only check chain match if specified on feature toggle
      (R.isNil(f.chains) || R.includes(chainName, f.chains)),
  );

  const [isWebFlowEnabled] = useFeature('validationFlow', (f: FeatureToggleValue): boolean => {
    const enabled =
      (f.isEnabled || false) && // the feature must be enabled
      R.includes(chainName, f.chains || []); // and the chain in the list of allowed chains
    return enabled;
  });

  const getCurrentCredentials = useCallback(
    async (inValidationId?: string | null): Promise<Result<CustomError, Credentials>> => {
      const useValidationId = inValidationId || validationId;
      if (!useValidationId) {
        return Result.failed(new TechnicalError('Unable to retrieve credentials, no validation Id set'));
      }

      const credentialsResult = await validationService.getCredentials({ validationId: useValidationId });
      if (credentialsResult.isFailed()) {
        return Result.failed(new TechnicalError('Unexpected error fetching credentials', credentialsResult.failure()));
      }

      return Result.successful(credentialsResult.success());
    },
    [validationId],
  );

  useEffect(() => {
    if (!validationId) {
      return;
    }
    metadataCollector.updateMetadata({ validationId });
  }, [validationId]);

  // If the current validation service can function in remote mode, switch over if the flow mode is set to partial or handover
  useEffect(() => {
    if (isRemoteModeSettable(validationService)) {
      const useRemoteMode = flowMode === FlowMode.partial || flowMode === FlowMode.handover;
      validationService.setRemoteMode(useRemoteMode);
    }
  }, [flowMode]);

  const backgroundInitialize = useCallback((): void => {
    backgroundInitialization.run();
  }, [backgroundInitialization]);

  /** Update error and status from latest validation result */
  const updateStateFromValidation = useCallback(
    (validation: Validation, eventBuilder: ValidationEventBuilder = validationEventBuilder): void => {
      let combinedErrors = validation.errorCodes || [];
      // Iterate through all steps and aggregate errors
      (validation.steps || []).forEach((step: Step) => {
        combinedErrors = R.concat(combinedErrors, step?.errorCodes || []);
      });

      setValidationErrors(R.uniq(combinedErrors));
      setValidationStatus(validation.status);
      // search for a failed step and record the first one found
      const failedStepSearch = R.find((step) => step.status === ValidationStepStatus.invalid, validation.steps);
      setFailedStep(failedStepSearch ? StepError.fromFailedStep(failedStepSearch) : null);
      if (!failedStepSearch && validation.status === ValidationStatus.completed) {
        eventBuilder.withCompletedValidation(validation);
      }
    },
    [validationEventBuilder],
  );

  /** Set the validation to failed step invalid and extract error codes if the given error is an invalid step error */
  const updateErrorStateFromFailedStep = useCallback(
    (stepErrorResult: Result<CustomError, unknown>, defaultToUnknownError = false): void => {
      if (!stepErrorResult.failedWith(isInvalidStepError)) {
        if (defaultToUnknownError) {
          setValidationStatus(ContextValidationStatus.unknownError);
        }

        return;
      }

      const stepError = stepErrorResult.failureAs<StepError>();
      setFailedStep(stepError);
      // populate the error codes from the invalid step result or set the generic error code if the flow is partial
      setValidationErrors(stepError.errorCodes);
      // Set the status to failed step invalid to force the status to be updated on the error screen
      setValidationStatus(ContextValidationStatus.failedStepInvalid);
    },
    [],
  );

  const waitForBackgroundProcessing = useCallback(async (): Promise<Result<CustomError>> => {
    const processingResult = await backgroundProcessing.waitForTasks();
    if (processingResult.isFailed()) {
      processingResult.logError('ValidationProvider: Error waiting for background tasks');
      updateErrorStateFromFailedStep(processingResult, true);
    }

    return processingResult;
  }, [backgroundProcessing, updateErrorStateFromFailedStep]);

  /** Internal function to get latest validation status and update state. Waits for any background processing before updating */
  const getValidationStatus = useCallback(
    async (shouldWaitForBackgroundProcessing = true, continuePolling = true): Promise<void> => {
      // If we have no valid validation id, then do not poll
      if (!validationId) {
        return;
      }

      // clear polling delay while we are fetching the latest status
      setPollingDelay(null);
      // wait for background processing if needed
      if (shouldWaitForBackgroundProcessing) {
        const processingResult = await waitForBackgroundProcessing();
        if (processingResult.isFailed()) {
          return;
        }
      }

      const getValidationResult = await validationService.getValidationResult({ validationId });
      if (getValidationResult.isFailed()) {
        getValidationResult.logError('ValidationProvider: Error getting validation status');
        setPollingDelay(continuePolling ? getPollingDelayMilliseconds() : null);
        return;
      }

      const validation = getValidationResult.success();
      // If the resulting status is unchanged, set the polling delay to try again
      if (validation.status === validationStatus) {
        setPollingDelay(continuePolling ? getPollingDelayMilliseconds() : null);
      }
      const userDataResult = await getUserData();
      if (userDataResult.isFailed()) {
        return;
      }
      const userData = userDataResult.success();
      unauthenticatedStatus.cacheStatus(userData.did, networkAddress, validation.status);
      updateStateFromValidation(validation);
    },
    [
      getUserData,
      networkAddress,
      updateStateFromValidation,
      validationId,
      validationStatus,
      waitForBackgroundProcessing,
    ],
  );
  const hasNewCredentialsToStore = useCallback(
    async (inValidationId?: string | null) => {
      const credentials = await getCurrentCredentials(inValidationId);
      const areAllCredentialsStored = await allCredentialsStored();
      // check if the credential ids in createdCredentials match the existing stored credentials
      // if they do, then we can skip the credential storage step
      const existingIds = existingStoredCredentials?.map(R.prop('credentialId')) || [];
      const createdIds = credentials.isSuccessful() ? credentials.success().credentials.map(R.prop('id')) : [];
      const newIds = R.difference(createdIds, existingIds);
      if ((!areAllCredentialsStored && createdIds.length > 0) || newIds.length > 0) {
        return true;
      }
      return false;
    },
    [allCredentialsStored, existingStoredCredentials, getCurrentCredentials],
  );

  const shouldInitiateCredentialStorage = useCallback(
    async (inValidationId?: string | null): Promise<boolean> => {
      if (!gknServiceData?.client?.credentialStorageEnabled?.prePassCreation) {
        return false;
      }
      if (validationStatus !== ValidationStatus.completed) {
        return false;
      }
      return hasNewCredentialsToStore(inValidationId);
    },
    [gknServiceData?.client?.credentialStorageEnabled?.prePassCreation, validationStatus, hasNewCredentialsToStore],
  );

  const updateValidationStatus = useCallback(async (): Promise<void> => {
    await getValidationStatus(false, false);
  }, [getValidationStatus]);

  // Start polling if validation status is poll, processing or review and we are not in a partial flow
  const pollingStatuses = useMemo(
    () => [ValidationStatus.processing, ValidationStatus.inReview, ContextValidationStatus.poll],
    [],
  );
  useEffect(() => {
    if (!validationId) {
      return;
    }

    if (flowMode !== FlowMode.partial && R.contains(validationStatus, pollingStatuses)) {
      setPollingDelay(getPollingDelayMilliseconds(validationStatus));
    } else {
      setPollingDelay(null);
    }
  }, [flowMode, pollingStatuses, validationId, validationStatus]);

  // Poll using getValidationStatus when pollingDelay is not null
  useInterval(() => getValidationStatus(), pollingDelay);

  /** Helper function to setup the initial terms state */
  const configureTerms = useCallback(
    (initialRoute: Routes) => {
      const flowAction = credentialExpired && action === Action.REFRESH ? Action.ISSUANCE : action;

      // skip this logic if the initial route is the declarations screen, or if this is not an issuance flow
      if (initialRoute === Routes.DECLARATIONS || flowAction !== Action.ISSUANCE) {
        return;
      }

      // If the initial route is not declarations, then we are resuming the flow and should include the terms in the final result
      const selfDeclaration = getGknLocalisedString(gknServiceData!, 'acceptedDeclaration');
      if (selfDeclaration) {
        setAcceptedDeclaration(t(selfDeclaration));
      }

      const requiredDeclarations = getGknValue<RequiredDeclarations>(gknServiceData!, 'requiredDeclarations');

      if (requiredDeclarations.acceptedTermsAndConditionsLink) {
        setAcceptedTermsAndConditionsLink(requiredDeclarations.acceptedTermsAndConditionsLink as string);
      }
      if (requiredDeclarations.acceptedPrivacyPolicyLink) {
        setAcceptedPrivacyPolicyLink(requiredDeclarations.acceptedPrivacyPolicyLink as string);
      }
      if (requiredDeclarations.acceptedBiometricsPolicyLink) {
        setAcceptedBiometricsLink(requiredDeclarations.acceptedBiometricsPolicyLink as string);
      }
    
    },
    [action, gknServiceData, setAcceptedBiometricsLink, setAcceptedDeclaration, setAcceptedTermsAndConditionsLink, setAcceptedPrivacyPolicyLink, t],
  );

  const getStatusUnauthenticated = useCallback(async (): Promise<Result<CustomError, string>> => {
    const userDataResult = await getUserData();
    if (userDataResult.isFailed()) {
      return Result.failed(userDataResult.failure());
    }
    const userData = userDataResult.success();
    const getStatusResult = await unauthenticatedStatus.getStatus(userData.did, networkAddress);
    if (getStatusResult.isFailed()) {
      return Result.failed(getStatusResult.failure());
    }

    const status = getStatusResult.success();
    if (status === ValidationStatus.completed && credentialExpired) {
      setForceNewValidation(true);
    }


    // Cache the resulting status so we can resume the flow correctly
    unauthenticatedStatus.cacheStatus(userData.did, networkAddress, status);
    return Result.successful(status);
  }, [getUserData, networkAddress]);

  const ensureValidationScopeRequest = useCallback(async (): Promise<Result<CustomError, ScopeRequest | null>> => {
    const flowAction = credentialExpired && action === Action.REFRESH ? Action.ISSUANCE : action;

    if (!validationScopeRequestEnabled || flowAction === Action.REFRESH) {
      Log.debug('ensureValidationScopeRequest: Scope request not enabled');
      return Result.successful(null);
    }

    // if no scope request template is configured then do not enable the scope request flow.
    // For the SIP provider, we use a locally-configured scope request template.
    // The new Gatekeeper Network service only specifies v3 templates (i.e. the web flow).
    const { scopeRequestTemplate: localConfigSrTemplateRef } = getProviderConfig<SipConfig>(provider || Provider.SIP);
    const srTemplateFromService = getGknValue<string>(gknServiceData!, 'scopeRequestRequiredTemplateId');
    if (provider === Provider.SIP && !localConfigSrTemplateRef) {
      Log.debug('Provider is equal to SIP and no locally configured scope request template, returning a failure');
      // SIP flows should always have a locally configured template ref.
      return Result.failed(new CustomError('SIP flow has no template id configured.'));
    }
    const scopeRequestTemplate = provider === Provider.SIP ? localConfigSrTemplateRef : srTemplateFromService;
    if (!scopeRequestTemplate) {
      Log.debug('No scope request template configured, returning null');
      return Result.successful(null);
    }

    // TODO CM-1834 check storage for existing scope request for this network and wallet and load if exists
    const result = await scopeRequestContext.createScopeRequest();
    Log.debug('createScopeRequest returned, returning result', result);
    return result;
  }, [getProviderConfig, gknServiceData, provider, scopeRequestContext, validationScopeRequestEnabled]);

  // When we're in mobile handoff mode we need to know that we're in cred reuse mode so the correct endpoint gets patched
  useEffect(() => {
    if (action === Action.LINK) {
      if (deviceLinkContext?.reuseCredentials === true && validationService instanceof DynamicValidationService) {
        validationService.setCredentialReuseMode(true, action);
      }

      if (deviceLinkContext?.sourceAction === Action.REFRESH && validationService instanceof DynamicValidationService) {
        validationService.setNetworkContext(networkName, deviceLinkContext.sourceAction);
      }
    }
  }, [action, gknServiceData?.clientFlowType, pendingSteps]);

  const startVerification = useCallback(
    async (
      scopeRequest: ScopeRequest | null,
    ): Promise<Result<CustomError, { route: Routes; validationId: string }>> => {
      flowInProgress(
        validationConfig.issuanceProviderMappings[networkName] || validationConfig.issuanceProviderMappings.default,
      );
      if (chosenCredentialIds.length > 0 && validationService instanceof DynamicValidationService) {
        const flowAction = credentialExpired && action === Action.REFRESH ? Action.ISSUANCE : action;
        validationService.setCredentialReuseMode(true, flowAction);
      }

      let validationResult = await validationService.create({ networkAddress });

      if (validationResult.isFailed()) {
        validationResult.logError('ValidationProvider: Error creating validation process');
        return Result.failed(validationResult.failure());
      }

      let validation = validationResult.success();

      // In the expired document credential refresh flow, we need to force a new validation
      if (forceNewValidation) {
        // Store the credentialExpired flag in local storage so the fees screen can display the correct fees
        setStorageItemForNetwork( StorageKey.CredentialExpired, networkAddress, 'true');
        await deleteVerification(true, false, validation.id);
        setForceNewValidation(false);

        validationResult = await validationService.create({ networkAddress });
        if (validationResult.isFailed()) {
          validationResult.logError('ValidationProvider: Error creating validation process');
          return Result.failed(validationResult.failure());
        }

        validation = validationResult.success();
      }

      setValidationId(validation.id);
      const flowAction = credentialExpired && action === Action.REFRESH ? Action.ISSUANCE : action;
      const eventBuilder = validationConfig.createEventBuilder(networkName, chosenCredentialIds.length > 0, flowAction);
      setValidationEventBuilder(eventBuilder);

      updateStateFromValidation(validation, eventBuilder);

      const initialFlowResult = validationRouting.getInitialFlowForValidation({
        validation,
        flowMode,
        scopeRequest: scopeRequest || undefined,
        showPIIShareAuthorization,
      });

      setShowContinueOnMobile(
        validationRouting.showContinueOnMobileSelector({
          validation,
          flowMode,
          continueOnMobileEnabled: enableContinueOnMobile,
        }),
      );

      if (validation.steps.length > 0) setCurrentStep(validation.steps[0].name);
      setPendingSteps(initialFlowResult.remainingSteps);
      configureTerms(initialFlowResult.route);
      backgroundInitialize();
      return Result.successful({ route: initialFlowResult.route, validationId: validation.id });
    },
    [
      backgroundInitialize,
      configureTerms,
      enableContinueOnMobile,
      flowInProgress,
      flowMode,
      networkAddress,
      networkName,
      showPIIShareAuthorization,
      updateStateFromValidation,
      chosenCredentialIds,
    ],
  );

  const updateValidationPendingSteps = useCallback(async (): Promise<Result<CustomError, ValidationStep[]>> => {
    if (!validationId) {
      return Result.failed(new TechnicalError('Unexpected null validation Id on updateValidationPendingSteps'));
    }

    const updatedValidationResult = await validationService.getValidationResult({ validationId });
    if (updatedValidationResult.isFailed()) {
      updatedValidationResult.logError('ValidationProvider: Error updating validation pending steps');
      return Result.failed(updatedValidationResult.failure());
    }

    const validation = updatedValidationResult.success();

    // The API returns the step as faceAuth, but if we're in a uniqueness flow, we need to rename it to uniqueness
    if (gknServiceData?.clientFlowType === 'uniqueness') {
      const renamedSteps: Step[] = R.map(
        R.ifElse(R.propEq('name', 'faceAuth'), R.assoc('name', 'uniqueness'), (step) => step),
        validation.steps,
      ) as Step[];
      validation.steps = renamedSteps;
    }

    const updatedPendingSteps = validationRouting.getValidationPendingSteps(validation.steps);
    setPendingSteps(updatedPendingSteps);
    const previousStepIdx = validation.steps.length - updatedPendingSteps.length - 1;
    if (previousStepIdx > 0 && validation.steps.length > 0) setCurrentStep(validation.steps[previousStepIdx].name);
    updateStateFromValidation(validation);
    // if the validation is failed or if any of the steps are invalid
    return ValidationError.isInErrorState(validation)
      ? Result.failed(ValidationError.from(validation))
      : Result.successful(updatedPendingSteps);
  }, [gknServiceData?.clientFlowType, updateStateFromValidation, validationId]);

  const activateDeviceLinkToken = useCallback(
    async (linkToken: string):Promise<LoginDeviceTokenResult> => {
      setFlowMode(FlowMode.partial);
      const authenticateResult = await loginWithDeviceLinkToken(linkToken);
      if (authenticateResult.isFailed()) {
        return authenticateResult;
      }

      const deviceLinkContextResponse = authenticateResult.success();
      setValidationId(deviceLinkContextResponse.validationId);
      setPendingSteps(deviceLinkContextResponse.steps);
      selectNetworkAddress(deviceLinkContextResponse.networkAddress);
      setDeviceLinkContext(deviceLinkContextResponse);
      setValidationStatus(ValidationStatus.collecting);

      return authenticateResult;
    },
    [loginWithDeviceLinkToken, selectNetworkAddress],
  );

  const getLandingRouteForUnauthenticatedUser = useCallback(async (): Promise<Routes> => {
    const wallet = getOptionalFlowExecutionConfig('wallet');
    const unauthenticatedRouteResult = await unauthenticatedRouting.getLandingRoute({
      networkName,
      networkAddress,
      action,
      isWebFlowEnabled,
      walletAddress: wallet,
      requiredDeclarations: getGknValue(gknServiceData!, 'requiredDeclarations'),
      credentialExpired,
    });
    if (unauthenticatedRouteResult.providerToSelect) {
      selectProvider(unauthenticatedRouteResult.providerToSelect);
    }
    return unauthenticatedRouteResult.route;
  }, [action, getUserData, gknServiceData, isWebFlowEnabled, networkAddress, networkName, selectProvider]);

  // the 'null' result represents the status where we 'hijack' the current validation process to store credentials
  // we will resume the flow after the credentials are stored by passing the route as a path in state
  const getRouteForNextStep = useCallback(async (): Promise<Routes | null> => {
    if (!isLoggedIn()) {
      const route = await getLandingRouteForUnauthenticatedUser();
      return route;
    }

    if (!validationId) {
      const scopeRequestResult = await ensureValidationScopeRequest();
      scopeRequestResult.logError('ValidationProvider: Error initializing scope request for validation flow');
      const useScopeRequest = scopeRequestResult.success();
      const flowAction = credentialExpired && action === Action.REFRESH ? Action.ISSUANCE : action;
      if (scopeRequestResult.isFailed()) {
        Log.error('ValidationProvider: Error initializing scope request for validation flow');
        return Routes.VALIDATION_ERROR;
      }
      if (
        useScopeRequest !== null && // a scope request is required for credential reuse
        flowAction === Action.ISSUANCE &&
        supportsCredentialReuse &&
        chosenCredentialIds.length === 0 &&
        ![ReuseStatus.failed, ReuseStatus.rejected, ReuseStatus.inProgress, ReuseStatus.notEligible].includes(
          reuseStatus as ReuseStatus,
        )
      ) {
        // navigate to the Choose credential reuse screen
        navigate(Routes.CHECK_FOR_EXISTING_CREDENTIALS);
        return null;
      }
      if (forceNewValidation) {
        await deleteVerification(true, false);
      }
      const initialRouteResult = await startVerification(useScopeRequest);
      initialRouteResult.logError('ValidationProvider: Error starting validation');
      // If starting the verification failed, show the error screen so the user can try again
      const { route, validationId: initialValidationId } = initialRouteResult.isSuccessful()
        ? initialRouteResult.success()
        : { route: Routes.VALIDATION_ERROR, validationId: undefined };

      const shouldStore = await shouldInitiateCredentialStorage(initialValidationId);
      if (shouldStore) {
        navigate(Routes.STORE_CREDENTIALS, { state: { prevPath: route } });
        return null;
      }
      return route;
    }

    const userDataResult = await getUserData();
    if (userDataResult.isSuccessful()) {
      const userData = userDataResult.success();
      unauthenticatedStatus.cacheStatus(userData.did, networkAddress, validationStatus);
    }

    // trigger polling if all pending steps are processed and the status is collecting
    if (R.isEmpty(pendingSteps) && validationStatus === ValidationStatus.collecting) {
      setValidationStatus(ContextValidationStatus.poll);
    }
    const nextFlowResult = validationRouting.getNextFlow(validationStatus, pendingSteps, flowMode);
    setCurrentStep(pendingSteps[0]);
    setPendingSteps(nextFlowResult.remainingSteps);
    const shouldStore = await shouldInitiateCredentialStorage(validationId);
    if (shouldStore) {
      navigate(Routes.STORE_CREDENTIALS, { state: { prevPath: nextFlowResult.route } });
      return null;
    }
    return nextFlowResult.route;
  }, [
    isLoggedIn,
    validationId,
    getUserData,
    pendingSteps,
    validationStatus,
    flowMode,
    shouldInitiateCredentialStorage,
    getLandingRouteForUnauthenticatedUser,
    ensureValidationScopeRequest,
    supportsCredentialReuse,
    reuseStatus,
    startVerification,
    navigate,
    networkAddress,
  ]);

  const resetPendingStepsForRoute = useCallback(
    (currentRoute: Routes): void => {
      const steps = validationRouting.getAllStepsForRoute(currentRoute);
      const updatedPendingSteps = R.insertAll(0, steps, pendingSteps);
      setPendingSteps(updatedPendingSteps);
    },
    [pendingSteps],
  );

  const deleteVerification = useCallback(
    async (skipOnPartialFlowMode = true, skipNotFailedValidation = true, validationIdForDeletion?: string): Promise<Result<CustomError>> => {
      const validationToDelete = validationIdForDeletion || validationId;
      if (!validationToDelete) {
        Log.debug('null validation Id on deleteVerification');
        return Result.noValueSuccess();
      }

      if (skipOnPartialFlowMode && flowMode === FlowMode.partial) {
        Log.debug('Skipping verification deletion check on partial flow');
        return Result.noValueSuccess();
      }

      let latestValidationStatus = validationStatus;
      // Fetch new status if the current validation status is failed with invalid step
      if (latestValidationStatus === ContextValidationStatus.failedStepInvalid) {
        const getValidationResult = await validationService.getValidationResult({ validationId: validationToDelete });
        if (getValidationResult.isFailed()) {
          Log.debug(`Could not retrieve the validation: ${validationId}`);
          return Result.noValueSuccess();
        }

        latestValidationStatus = getValidationResult.success().status;
        setValidationStatus(latestValidationStatus);
      }

      if (skipNotFailedValidation && latestValidationStatus !== ValidationStatus.failed) {
        Log.debug(`Not deleting validation as current status is not failed. Status: ${latestValidationStatus}`);
        return Result.noValueSuccess();
      }

      const deleteValidationResult = await validationService.deleteValidation({ validationId: validationToDelete });
      if (deleteValidationResult.isFailed()) {
        deleteValidationResult.logError('ValidationProvider: Error deleting validation');
        return Result.noValueFailed(deleteValidationResult.failure());
      }

      const userDataResult = await getUserData();
      if (userDataResult.isSuccessful()) {
        const userData = userDataResult.success();
        // If we're deleting the validation, also delete the cached status
        unauthenticatedStatus.clearCachedStatus(userData.did, networkAddress);
      }
      return Result.noValueSuccess();
    },
    [flowMode, getUserData, networkAddress, validationId, validationStatus],
  );

  const processStep = useCallback(
    async (stepWorker: StepWorker): Promise<CancellableResult<CustomError>> => {
      if (!validationId) {
        return CancellableResult.failed(new TechnicalError('Unexpected null validation Id on processStep'));
      }

      // wait for background initialization if step requires it
      if (stepWorker.requiresInit) {
        await backgroundInitialization.wait();
      }

      const { result, backgroundTask } = await stepWorker.processStep(validationId);
      result.logError('ValidationProvider: Error processing step');
      if (result.isFailed()) {
        updateErrorStateFromFailedStep(result);
        return result;
      }

      if (backgroundTask) {
        backgroundProcessing.addTask(backgroundTask);
      }

      return result;
    },
    [backgroundInitialization, backgroundProcessing, updateErrorStateFromFailedStep, validationId],
  );

  // the 'null' result represents the status where we 'hijack' the current validation process to store credentials
  // we will resume the flow after the credentials are stored by passing the route as a path in state
  const getRouteForCurrentStatus = useCallback(async (): Promise<Routes | null> => {
    const route = validationRouting.getRouteForValidationStatus({
      status: validationStatus,
      scopeRequest: scopeRequestContext.scopeRequest || undefined,
      showPIIShareAuthorization,
    });
    const shouldStore = await shouldInitiateCredentialStorage();
    if (shouldStore) {
      navigate(Routes.STORE_CREDENTIALS, { state: { prevPath: route } });
      return null;
    }
    return route;
  }, [
    validationStatus,
    scopeRequestContext.scopeRequest,
    showPIIShareAuthorization,
    shouldInitiateCredentialStorage,
    navigate,
  ]);

  const getEventPayload = useCallback(async (): Promise<EventPayloadResult> => {
    let prov =
      validationConfig.issuanceProviderMappings[networkName] || validationConfig.issuanceProviderMappings.default;
    // include scope request Id and set provider to SIP if enabled and scope request exists
    if (validationScopeRequestEnabled && scopeRequestContext.scopeRequest) {
      prov = Provider.SIP;
      validationEventBuilder.withScopeRequestPayload(scopeRequestContext.getEventPayload());
    }

    const tokenResult = await currentToken();
    if (tokenResult.isFailed()) {
      return Result.failed(tokenResult.failure());
    }

    validationEventBuilder.withAuthToken(tokenResult.success());
    validationEventBuilder.withProvider(prov);
    const eventPayload = validationEventBuilder.build();
    return Result.successful(eventPayload);
  }, [currentToken, networkName, scopeRequestContext, validationEventBuilder, validationScopeRequestEnabled]);

  const verificationStarted = useCallback((): boolean => {
    return !R.isNil(validationId) && !R.isEmpty(validationId);
  }, [validationId]);

  const getPendingContinueOnMobileRoutes = useCallback((): Array<Routes> => {
    const steps = currentStep ? [currentStep, ...pendingSteps] : pendingSteps;
    return validationRouting.getPendingContinueOnMobileRoutes(steps);
  }, [currentStep, pendingSteps]);

  const processLinkedDeviceConnected = useCallback((): void => {
    if (currentStep) setPendingSteps([currentStep, ...pendingSteps]);
  }, [currentStep, pendingSteps]);

  const doFetchStatus = useCallback(async () => {
    const statusResult = await getStatusUnauthenticated();
    if (statusResult.isFailed()) {
      flowFailed();
      return;
    }

    const status = statusResult.success();
    // pre-fetch the validationId for use in credential retrieval if the status is complete
    if (status === ValidationStatus.completed) {
      if (isLoggedIn()) {
        validationStatusFetchComplete(new StatusEventPayload(status));
        const validationResult = await validationService.create({ networkAddress });
        if (validationResult.isFailed()) {
          validationResult.logError('ValidationProvider: Error creating validation process');
          return;
        }

        const validation = validationResult.success();
        setValidationId(validation.id);
      }
    }
  }, [flowFailed, getStatusUnauthenticated, networkAddress, validationStatusFetchComplete, isLoggedIn]);

  useEffect(() => {
    if (!initialized) {
      return;
    }
    if (gknServiceData?.clientFlowType !== 'document') {
      return;
    }

    if (!fetchStatus) {
      setFetchStatus('in_progress');
      doFetchStatus().finally(() => setFetchStatus('complete'));
    }
  }, [initialized, isLoggedIn, fetchStatus, gknServiceData?.clientFlowType, doFetchStatus]);

  const value = useMemo(
    () => ({
      isWebFlowEnabled,
      validationId,
      validationErrors,
      backgroundInitialize,
      getStatusUnauthenticated,
      activateDeviceLinkToken,
      updateValidationPendingSteps,
      getRouteForNextStep,
      resetPendingStepsForRoute,
      deleteVerification,
      getEventPayload,
      verificationStarted,
      getRouteForCurrentStatus,
      validationStatus,
      updateValidationStatus,
      waitForBackgroundProcessing,
      setFlowMode,
      flowMode,
      getPendingContinueOnMobileRoutes,
      processStep,
      failedStep,
      showContinueOnMobile,
      processLinkedDeviceConnected,
      getCurrentCredentials,
      fetchStatus,
      shouldInitiateCredentialStorage,
      hasNewCredentialsToStore,
      deviceLinkContext,
    }),
    [
      isWebFlowEnabled,
      validationId,
      validationErrors,
      backgroundInitialize,
      getStatusUnauthenticated,
      activateDeviceLinkToken,
      updateValidationPendingSteps,
      getRouteForNextStep,
      resetPendingStepsForRoute,
      deleteVerification,
      getEventPayload,
      verificationStarted,
      getRouteForCurrentStatus,
      validationStatus,
      updateValidationStatus,
      waitForBackgroundProcessing,
      setFlowMode,
      flowMode,
      getPendingContinueOnMobileRoutes,
      processStep,
      failedStep,
      showContinueOnMobile,
      processLinkedDeviceConnected,
      getCurrentCredentials,
      fetchStatus,
      shouldInitiateCredentialStorage,
      hasNewCredentialsToStore,
      deviceLinkContext,
    ],
  );
  return <ValidationContext.Provider value={value}>{children}</ValidationContext.Provider>;
}

export const useValidation = (): ValidationProps => useContext(ValidationContext);
